import React, { useEffect } from 'react';
import { StyleSheet } from 'react-native';
import { Container, Box, Typography, Button } from '@mui/material';
import { Add } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import ExternalAccountTrigger from '../../components/ExternalAccount/ExternalAccountTrigger';
import ExternalAccountList from '../../components/ExternalAccount/ExternalAccountList';
import { getUserExternalAccounts } from '../../redux/account/actions';
import { RootState } from '../../redux/root-reducer';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';

const styles = StyleSheet.create({
    container: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
    },
});

const AccountExternalBankAccounts = () => {
    const dispatch = useDispatch();
    const { themeStyle } = useThemeContext();
    const { userExternalAccounts, gettingUserExternalAccounts } = useSelector((state: RootState) => state.account);
    const [showAddModal, setShowAddModal] = React.useState(false);

    useEffect(() => {
        // Fetch user's external accounts when component mounts
        dispatch(getUserExternalAccounts());
    }, [dispatch]);

    const handleAddAccount = () => {
        setShowAddModal(true);
    };

    const handleCloseModal = () => {
        setShowAddModal(false);
    };

    return (
        <Container disableGutters style={styles.container} maxWidth={false}>
            <Box sx={{ width: '100%', maxWidth: 1200, p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography
                        variant="h4"
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            color: theme[themeStyle].tag_white,
                        }}>
                        External Bank Accounts
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={handleAddAccount}
                        disabled={userExternalAccounts && userExternalAccounts.length >= 3}
                        sx={{
                            backgroundColor: theme[themeStyle].primary_orange,
                            color: theme[themeStyle].black,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            '&:hover': {
                                backgroundColor: theme[themeStyle].highlight,
                            },
                            '&:disabled': {
                                backgroundColor: theme[themeStyle].form_divider,
                                color: theme[themeStyle].placeholderGray,
                            },
                        }}>
                        Add Account
                    </Button>
                </Box>

                {userExternalAccounts && userExternalAccounts.length >= 3 && (
                    <Box sx={{ mb: 2 }}>
                        <Typography
                            variant="body2"
                            sx={{
                                color: '#f44336',
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                            }}>
                            Maximum 3 accounts reached. Please delete an existing account to add a new one.
                        </Typography>
                    </Box>
                )}

                {gettingUserExternalAccounts ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                        <Typography
                            variant="body1"
                            sx={{
                                color: theme[themeStyle].light_gray_text,
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                            }}>
                            Loading accounts...
                        </Typography>
                    </Box>
                ) : (
                    <ExternalAccountList accounts={userExternalAccounts || []} />
                )}

                <ExternalAccountTrigger
                    showPageLayout={userExternalAccounts && userExternalAccounts.length < 1}
                    open={showAddModal}
                    onClose={handleCloseModal}
                    hideButton={userExternalAccounts && userExternalAccounts.length > 1}
                />
            </Box>
        </Container>
    );
};

export { AccountExternalBankAccounts };
